/** Conversational edit flow for terminal assistant. */
import {
  capitalizeName,
  parseSmartDate,
  parseSmartTime,
  isSkip,
  upperDoc,
} from './assistant-ticket-new.js';

const NOT_INTERESTED_REASON_LABELS = {
  declined: 'Client declined quote',
  paused: 'Paused — may contact later',
  other: 'Other',
};
import { formatLeadDetailHtml } from './assistant-lead-format.js';
import { ticketPricingFromExtra } from './utils.js';

export function initEditState(lead) {
  return {
    lead,
    field: null,
    paxTarget: null,
    paxField: null,
  };
}

const EDIT_MENU_BY_TYPE = {
  ticket: [
    { key: 'name', label: 'contact name', match: (s) => s === '1' || s === 'name' || s.includes('contact name') },
    { key: 'phone', label: 'phone', match: (s) => s === '2' || s === 'phone' },
    { key: 'note', label: 'note', match: (s) => s === '3' || s === 'note' },
    { key: 'pnr', label: 'pnr', match: (s) => s === '4' || s === 'pnr' },
    { key: 'source', label: 'source', match: (s) => s === '5' || s === 'source' },
    { key: 'airline', label: 'airline', match: (s) => s === '6' || s === 'airline' },
    {
      key: 'passengers',
      label: 'passengers / pricing',
      match: (s) => s === '7' || s.includes('passenger') || s.includes('pax') || s.includes('passengers'),
    },
    {
      key: 'not_interested',
      label: 'client not interested',
      match: (s) => s === '8' || s === 'ni' || s.includes('not interested') || s.includes('not_interested'),
    },
  ],
  visa: [
    { key: 'name', label: 'contact name', match: (s) => s === '1' || s === 'name' || s.includes('contact name') },
    { key: 'phone', label: 'phone', match: (s) => s === '2' || s === 'phone' },
    { key: 'note', label: 'note', match: (s) => s === '3' || s === 'note' },
    { key: 'source', label: 'source', match: (s) => s === '4' || s === 'source' },
    { key: 'package', label: 'visa package', match: (s) => s === '5' || s === 'package' || s.includes('visa') },
    { key: 'passport', label: 'passport number', match: (s) => s === '6' || s === 'passport' },
    {
      key: 'not_interested',
      label: 'client not interested',
      match: (s) => s === '7' || s === 'ni' || s.includes('not interested') || s.includes('not_interested'),
    },
  ],
  tour: [
    { key: 'name', label: 'contact name', match: (s) => s === '1' || s === 'name' || s.includes('contact name') },
    { key: 'phone', label: 'phone', match: (s) => s === '2' || s === 'phone' },
    { key: 'note', label: 'note', match: (s) => s === '3' || s === 'note' },
    { key: 'source', label: 'source', match: (s) => s === '4' || s === 'source' },
    { key: 'package', label: 'tour package', match: (s) => s === '5' || s === 'package' || s === 'tour' },
    { key: 'duration', label: 'duration', match: (s) => s === '6' || s.includes('duration') || /\d+\s*n/.test(s) },
    { key: 'custom_req', label: 'custom requirements', match: (s) => s === '7' || s.includes('custom') || s.includes('requirement') },
    {
      key: 'not_interested',
      label: 'client not interested',
      match: (s) => s === '8' || s === 'ni' || s.includes('not interested') || s.includes('not_interested'),
    },
  ],
};

function leadTypeOf(leadOrType) {
  if (typeof leadOrType === 'string') return leadOrType;
  return leadOrType?.type || 'ticket';
}

export function getEditMenuItems(leadOrType) {
  const type = leadTypeOf(leadOrType);
  return EDIT_MENU_BY_TYPE[type] || EDIT_MENU_BY_TYPE.ticket;
}

export function editMenuMaxChoice(leadOrType) {
  return getEditMenuItems(leadOrType).length;
}

export function editMenuText(leadOrType) {
  const items = getEditMenuItems(leadOrType);
  const lines = [
    'Edit which field?',
    ...items.map((item, i) => `  ${i + 1} ${item.label}`),
    '  done — return to inquiry',
  ];
  return lines.join('\n');
}

export function parseEditChoice(raw, leadOrType) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'done' || s === 'back' || s === 'ig') return 'done';
  const items = getEditMenuItems(leadOrType);
  const num = Number(s);
  if (Number.isInteger(num) && num >= 1 && num <= items.length) {
    return items[num - 1].key;
  }
  for (const item of items) {
    if (item.match(s)) return item.key;
  }
  return null;
}

export function parseNotInterestedReason(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === '1' || s.includes('declin')) return 'declined';
  if (s === '2' || s.includes('pause')) return 'paused';
  if (s === '3' || s === 'other') return 'other';
  return null;
}

/** @returns {object[]} conversational steps for closing as client not interested */
export function notInterestedSteps(leadType) {
  if (leadType === 'ticket') {
    return [
      {
        key: 'reason',
        ask: 'Why is the client not interested?',
        hint: '  1 declined quote · 2 paused — may contact later · 3 other',
        validate: (v) => (!parseNotInterestedReason(v)
          ? 'Pick 1, 2, or 3 (or type declined / paused / other).'
          : null),
        parse: (v) => parseNotInterestedReason(v),
      },
      {
        key: 'note',
        ask: 'Notes for future contact?',
        hint: 'Required when reason is other · skip otherwise',
        optional: true,
        validate: (v, answers) => {
          if (answers.reason === 'other' && (!String(v).trim() || isSkip(v))) {
            return 'Enter notes when reason is other.';
          }
          return null;
        },
        parse: (v) => (isSkip(v) ? null : String(v).trim()),
      },
    ];
  }
  return [
    {
      key: 'note',
      ask: 'Why is the client not interested?',
      hint: 'Required',
      validate: (v) => (!String(v).trim() ? 'Enter the reason.' : null),
      parse: (v) => String(v).trim(),
    },
  ];
}

export function buildNotInterestedPatchBody(leadType, answers = {}) {
  if (leadType === 'ticket') {
    const reason = answers.reason;
    const note = answers.note || NOT_INTERESTED_REASON_LABELS[reason] || reason;
    return {
      status: 'client_not_interested',
      note,
      stage_data: { reason, note },
    };
  }
  return {
    status: 'client_not_interested',
    note: answers.note,
    stage_data: { note: answers.note },
  };
}

export function validateNotInterestedAnswers(leadType, answers = {}) {
  const body = buildNotInterestedPatchBody(leadType, answers);
  if (!body.note) return 'Enter the reason.';
  if (leadType === 'ticket' && !answers.reason) {
    return 'Pick reason: 1 declined · 2 paused · 3 other';
  }
  return null;
}

function syncExtraPricing(extra) {
  const out = { ...extra };
  if (out.adt_profiles?.length) {
    out.adt_pricing = out.adt_profiles.map((p) => ({
      name: p.name,
      cost: p.cost ?? null,
      profit: p.profit ?? null,
    }));
    out.per_adt_itineraries = out.per_adt_itineraries ?? true;
  }
  if (out.stu_profiles?.length) {
    out.stu_pricing = out.stu_profiles.map((p) => ({
      name: p.name,
      cost: p.cost ?? null,
      profit: p.profit ?? null,
    }));
  }
  return out;
}

function applyHeaderFromFirstProfile(lead, extra) {
  const first = extra.adt_profiles?.[0];
  if (!first) return {};
  return {
    trip_type: first.trip_type || lead.trip_type || 'one_way',
    origin: first.origin || lead.origin || null,
    destination: first.destination || lead.destination || null,
    depart_date: first.depart_date || lead.depart_date || null,
    return_date: first.trip_type === 'return' ? (first.return_date || null) : null,
  };
}

export function recalcLeadFromExtra(lead, extra) {
  const synced = syncExtraPricing(extra);
  const pricing = ticketPricingFromExtra(synced);
  const header = applyHeaderFromFirstProfile(lead, synced);
  return {
    extra: {
      ...synced,
      quoted_total: pricing?.total ?? synced.quoted_total ?? lead.quote_amount,
    },
    quote_amount: pricing?.total ?? lead.quote_amount,
    cost_amount: pricing?.cost ?? lead.cost_amount,
    ...header,
  };
}

export function buildLeadUpdateBody(lead, changes, agentId) {
  const extra = syncExtraPricing({ ...(lead.extra || {}), ...(changes.extra || {}) });
  const recalc = changes.recalcPricing ? recalcLeadFromExtra(lead, extra) : null;
  const mergedExtra = recalc?.extra ?? extra;

  const body = {
    type: lead.type || 'ticket',
    name: changes.name ?? lead.name,
    phone: changes.phone ?? lead.phone ?? lead.phone_display,
    source: changes.source ?? lead.source,
    status: lead.status,
    agent_id: agentId,
    inquiry_date: lead.inquiry_date,
    followup_date: lead.followup_date,
    origin: recalc?.origin ?? changes.origin ?? lead.origin,
    destination: recalc?.destination ?? changes.destination ?? lead.destination,
    depart_date: recalc?.depart_date ?? changes.depart_date ?? lead.depart_date,
    return_date: recalc?.return_date ?? changes.return_date ?? lead.return_date,
    trip_type: recalc?.trip_type ?? changes.trip_type ?? lead.trip_type,
    airline: changes.airline !== undefined ? changes.airline : lead.airline,
    pnr: changes.pnr !== undefined ? changes.pnr : lead.pnr,
    package_name: changes.package_name !== undefined ? changes.package_name : lead.package_name,
    custom_req: changes.custom_req !== undefined ? changes.custom_req : lead.custom_req,
    is_custom: changes.is_custom !== undefined ? changes.is_custom : lead.is_custom,
    ticket_class: lead.ticket_class,
    quote_amount: recalc?.quote_amount ?? changes.quote_amount ?? lead.quote_amount,
    cost_amount: recalc?.cost_amount ?? changes.cost_amount ?? lead.cost_amount,
    amount: changes.amount ?? recalc?.quote_amount ?? lead.amount,
    sold_amount: lead.sold_amount,
    note_text: changes.note_text !== undefined ? changes.note_text : undefined,
    extra: mergedExtra,
  };
  return body;
}

export function formatEditSummary(lead) {
  return formatLeadDetailHtml(lead, { access: { can_edit: true } });
}

/** @returns {{ id: string, kind: string, index?: number, label: string, name?: string }[]} */
export function listPassengerTargets(lead) {
  const extra = lead.extra || {};
  const pax = extra.pax || {};
  const targets = [];

  (extra.adt_profiles || []).forEach((pr, i) => {
    const name = String(pr.name || '').trim();
    targets.push({
      id: `adt:${i}`,
      kind: 'adt',
      index: i,
      label: `ADT ${i + 1}${name ? ` — ${name}` : ''}`,
      name,
    });
  });

  (extra.stu_profiles || []).forEach((pr, i) => {
    const name = String(pr.name || '').trim();
    targets.push({
      id: `stu:${i}`,
      kind: 'stu',
      index: i,
      label: `STU ${i + 1}${name ? ` — ${name}` : ''}`,
      name,
    });
  });

  if ((pax.chd || 0) > 0) {
    targets.push({ id: 'chd:rate', kind: 'chd_rate', label: `CHD rates (×${pax.chd})` });
  }
  if ((pax.inf || 0) > 0) {
    targets.push({ id: 'inf:rate', kind: 'inf_rate', label: `INF rates (×${pax.inf})` });
  }

  return targets;
}

export function passengerPickMenuText(lead) {
  const targets = listPassengerTargets(lead);
  if (!targets.length) {
    return 'No per-passenger data on this inquiry.\nType back to return.';
  }
  const lines = ['Edit which passenger?', ...targets.map((t, i) => `  ${i + 1} ${t.label}`), '  back'];
  return lines.join('\n');
}

export function parsePassengerPick(raw, lead) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'back' || s === 'done' || s === 'ig') return 'back';
  const targets = listPassengerTargets(lead);
  const num = Number(s);
  if (Number.isInteger(num) && num >= 1 && num <= targets.length) {
    return targets[num - 1];
  }
  return null;
}

const PROFILE_FIELD_DEFS = [
  { id: 'name', label: 'name', hint: 'Full name as on passport.' },
  { id: 'origin', label: 'origin', hint: 'IATA code, e.g. CMB' },
  { id: 'destination', label: 'destination', hint: 'IATA code, e.g. DXB' },
  { id: 'depart_date', label: 'depart date', hint: 'YYMMDD or YYYY-MM-DD' },
  { id: 'return_date', label: 'return date', hint: 'YYMMDD or YYYY-MM-DD', whenProfile: (pr) => pr?.trip_type === 'return' },
  { id: 'depart_time', label: 'depart time', hint: 'HHMM or HH:MM (max 23:59)' },
  { id: 'arrive_time', label: 'arrive time', hint: 'HHMM or HH:MM (max 23:59)' },
  { id: 'cost', label: 'net fare (LKR)', hint: 'Net cost for this passenger' },
  { id: 'profit', label: 'profit (LKR)', hint: 'Markup for this passenger' },
];

const RATE_FIELD_DEFS = [
  { id: 'cost', label: 'net fare per person (LKR)' },
  { id: 'profit', label: 'profit per person (LKR)' },
];

function activeProfileFields(profile) {
  return PROFILE_FIELD_DEFS.filter((f) => !f.whenProfile || f.whenProfile(profile));
}

function activeRateFields() {
  return RATE_FIELD_DEFS;
}

export function passengerFieldMenuText(target, lead) {
  const extra = lead.extra || {};
  let fields;
  let title;

  if (target.kind === 'adt' || target.kind === 'stu') {
    const profiles = target.kind === 'adt' ? extra.adt_profiles : extra.stu_profiles;
    const profile = profiles?.[target.index];
    fields = activeProfileFields(profile || {});
    title = target.label;
  } else {
    fields = activeRateFields();
    title = target.label;
  }

  const lines = [`Edit ${title}:`, ...fields.map((f, i) => `  ${i + 1} ${f.label}`)];
  const removeErr = canRemovePassengerTarget(lead, target);
  if (!removeErr) {
    lines.push(`  remove — ${passengerRemoveActionLabel(target, lead)}`);
  }
  lines.push('  back');
  return lines.join('\n');
}

export function canRemovePassengerTarget(lead, target) {
  const extra = lead.extra || {};
  const pax = extra.pax || {};

  if (target.kind === 'adt') {
    const count = (extra.adt_profiles || []).length;
    if (count <= 1) return 'Cannot remove the only adult passenger.';
    return null;
  }
  if (target.kind === 'stu') {
    if (!(extra.stu_profiles || []).length) return 'No student passenger to remove.';
    return null;
  }
  if (target.kind === 'chd_rate') {
    if (!(pax.chd > 0)) return 'No child passengers on this inquiry.';
    return null;
  }
  if (target.kind === 'inf_rate') {
    if (!(pax.inf > 0)) return 'No infant passengers on this inquiry.';
    return null;
  }
  return 'Cannot remove this passenger.';
}

export function passengerRemoveActionLabel(target, lead) {
  const pax = lead.extra?.pax || {};
  if (target.kind === 'chd_rate') {
    return (pax.chd || 0) > 1 ? 'remove 1 CHD' : 'remove CHD';
  }
  if (target.kind === 'inf_rate') {
    return (pax.inf || 0) > 1 ? 'remove 1 INF' : 'remove INF';
  }
  return 'remove this passenger';
}

export function passengerRemoveConfirmText(target, lead) {
  const label = target.label || 'this passenger';
  const action = passengerRemoveActionLabel(target, lead);
  return `${action.charAt(0).toUpperCase()}${action.slice(1)} — ${label}? (Y/N)`;
}

export function listPassengerFieldChoices(target, lead) {
  const extra = lead.extra || {};
  let fields;
  if (target.kind === 'adt' || target.kind === 'stu') {
    const profiles = target.kind === 'adt' ? extra.adt_profiles : extra.stu_profiles;
    fields = activeProfileFields(profiles?.[target.index] || {});
  } else {
    fields = activeRateFields();
  }
  return fields.map((f, i) => ({ label: f.label, value: String(i + 1) }));
}

export function parsePassengerFieldPick(raw, target, lead) {
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'back' || s === 'done' || s === 'ig') return 'back';
  if (s === 'remove' || s === 'r' || s === 'delete' || s === 'del') {
    return canRemovePassengerTarget(lead, target) ? null : '__remove__';
  }

  let fields;
  if (target.kind === 'adt' || target.kind === 'stu') {
    const extra = lead.extra || {};
    const profiles = target.kind === 'adt' ? extra.adt_profiles : extra.stu_profiles;
    fields = activeProfileFields(profiles?.[target.index] || {});
  } else {
    fields = activeRateFields();
  }

  const num = Number(s);
  if (Number.isInteger(num) && num >= 1 && num <= fields.length) {
    return fields[num - 1].id;
  }
  return null;
}

export function passengerFieldPrompt(target, fieldId, lead) {
  const extra = lead.extra || {};
  if (target.kind === 'adt' || target.kind === 'stu') {
    const profiles = target.kind === 'adt' ? extra.adt_profiles : extra.stu_profiles;
    const profile = profiles?.[target.index] || {};
    const def = PROFILE_FIELD_DEFS.find((f) => f.id === fieldId);
    const cur = profile[fieldId];
    const curText = cur != null && cur !== '' ? ` (now: ${cur})` : '';
    return {
      question: `New ${def?.label || fieldId}${curText}?`,
      hint: def?.hint || null,
    };
  }
  const rates = extra.pax_cost_rates || {};
  const profits = extra.pax_profit_rates || {};
  const key = target.kind === 'chd_rate' ? 'chd' : 'inf';
  const cur = fieldId === 'cost' ? rates[key] : profits[key];
  const def = RATE_FIELD_DEFS.find((f) => f.id === fieldId);
  const curText = cur != null ? ` (now: ${cur})` : '';
  return {
    question: `New ${def?.label || fieldId}${curText}?`,
    hint: 'Amount in LKR',
  };
}

export function validatePassengerFieldValue(target, fieldId, raw, lead) {
  const v = String(raw || '').trim();
  if (target.kind === 'adt' || target.kind === 'stu') {
    switch (fieldId) {
      case 'name':
        if (!v) return 'Name is required.';
        return null;
      case 'origin':
      case 'destination':
        if (!/^[A-Z]{3}$/.test(v.toUpperCase())) return 'Enter 3-letter airport code.';
        return null;
      case 'depart_date':
      case 'return_date':
        if (!parseSmartDate(v)) return 'Invalid date. Use YYMMDD or YYYY-MM-DD.';
        return null;
      case 'depart_time':
      case 'arrive_time':
        if (!parseSmartTime(v)) return 'Invalid time. Use HHMM or HH:MM between 00:00–23:59.';
        return null;
      case 'cost':
      case 'profit': {
        const n = Number(v.replace(/,/g, ''));
        if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR (0 or more).';
        return null;
      }
      default:
        return null;
    }
  }
  if (fieldId === 'cost' || fieldId === 'profit') {
    const n = Number(v.replace(/,/g, ''));
    if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR (0 or more).';
    return null;
  }
  return null;
}

export function parsePassengerFieldValue(target, fieldId, raw) {
  const v = String(raw || '').trim();
  if (target.kind === 'adt' || target.kind === 'stu') {
    switch (fieldId) {
      case 'name':
        return capitalizeName(v);
      case 'origin':
      case 'destination':
        return v.toUpperCase();
      case 'depart_date':
      case 'return_date':
        return parseSmartDate(v);
      case 'depart_time':
      case 'arrive_time':
        return parseSmartTime(v);
      case 'cost':
      case 'profit':
        return Number(v.replace(/,/g, ''));
      default:
        return v;
    }
  }
  return Number(v.replace(/,/g, ''));
}

function pruneRateMaps(extra) {
  const out = { ...extra };
  if (out.pax_cost_rates && !Object.keys(out.pax_cost_rates).length) out.pax_cost_rates = null;
  if (out.pax_profit_rates && !Object.keys(out.pax_profit_rates).length) out.pax_profit_rates = null;
  return out;
}

/** @returns {object} partial changes for buildLeadUpdateBody */
export function buildPassengerRemovalChanges(lead, target) {
  const extra = { ...(lead.extra || {}) };
  const pax = {
    adt: extra.pax?.adt ?? (extra.adt_profiles?.length || 1),
    stu: extra.pax?.stu ?? (extra.stu_profiles?.length || 0),
    chd: extra.pax?.chd ?? 0,
    inf: extra.pax?.inf ?? 0,
  };

  if (target.kind === 'adt') {
    const profiles = [...(extra.adt_profiles || [])];
    profiles.splice(target.index, 1);
    extra.adt_profiles = profiles;
    pax.adt = profiles.length;
    extra.per_adt_itineraries = profiles.length > 0;
  } else if (target.kind === 'stu') {
    const profiles = [...(extra.stu_profiles || [])];
    profiles.splice(target.index, 1);
    pax.stu = profiles.length;
    extra.stu_profiles = profiles.length ? profiles : null;
    extra.stu_pricing = profiles.length
      ? profiles.map((p) => ({ name: p.name, cost: p.cost ?? null, profit: p.profit ?? null }))
      : null;
  } else if (target.kind === 'chd_rate') {
    pax.chd = Math.max(0, (pax.chd || 0) - 1);
    if (pax.chd === 0) {
      const costRates = { ...(extra.pax_cost_rates || {}) };
      const profitRates = { ...(extra.pax_profit_rates || {}) };
      delete costRates.chd;
      delete profitRates.chd;
      extra.pax_cost_rates = Object.keys(costRates).length ? costRates : null;
      extra.pax_profit_rates = Object.keys(profitRates).length ? profitRates : null;
    }
  } else if (target.kind === 'inf_rate') {
    pax.inf = Math.max(0, (pax.inf || 0) - 1);
    if (pax.inf === 0) {
      const costRates = { ...(extra.pax_cost_rates || {}) };
      const profitRates = { ...(extra.pax_profit_rates || {}) };
      delete costRates.inf;
      delete profitRates.inf;
      extra.pax_cost_rates = Object.keys(costRates).length ? costRates : null;
      extra.pax_profit_rates = Object.keys(profitRates).length ? profitRates : null;
    }
  }

  extra.pax = pax;
  return { extra: pruneRateMaps(extra), recalcPricing: true };
}

/** @returns {object} partial changes for buildLeadUpdateBody */
export function applyPassengerFieldEdit(lead, target, fieldId, value) {
  const extra = { ...(lead.extra || {}) };

  if (target.kind === 'adt' || target.kind === 'stu') {
    const key = target.kind === 'adt' ? 'adt_profiles' : 'stu_profiles';
    const profiles = [...(extra[key] || [])];
    const profile = { ...(profiles[target.index] || {}) };
    profile[fieldId] = value;
    profiles[target.index] = profile;
    extra[key] = profiles;
  } else {
    const rateKey = target.kind === 'chd_rate' ? 'chd' : 'inf';
    const costRates = { ...(extra.pax_cost_rates || {}) };
    const profitRates = { ...(extra.pax_profit_rates || {}) };
    if (fieldId === 'cost') costRates[rateKey] = value;
    else profitRates[rateKey] = value;
    extra.pax_cost_rates = costRates;
    extra.pax_profit_rates = profitRates;
  }

  return { extra, recalcPricing: true };
}

function fieldDisplayValue(field, lead) {
  if (!lead) return null;
  switch (field) {
    case 'name': return lead.name;
    case 'phone': return lead.phone || lead.phone_display;
    case 'note': return lead.note || lead.latest_note;
    case 'pnr': return lead.pnr;
    case 'source': return lead.source;
    case 'airline': return lead.airline;
    case 'package': return lead.package_name;
    case 'passport': return lead.extra?.passport_no;
    case 'duration': return lead.extra?.tour_duration;
    case 'custom_req': return lead.custom_req;
    default: return null;
  }
}

export function simpleFieldPrompt(field, lead) {
  const type = lead?.type || 'ticket';
  const cur = fieldDisplayValue(field, lead);
  const curText = cur != null && cur !== '' ? ` (now: ${cur})` : '';
  const prompts = {
    name: { question: `New contact name${curText}?`, hint: null },
    phone: { question: `New phone${curText}?`, hint: null },
    note: { question: `New note${curText}?`, hint: 'skip to clear' },
    pnr: { question: `New PNR${curText}?`, hint: null },
    source: { question: `New source${curText}?`, hint: 'Pick number or type name' },
    airline: { question: `New airline${curText}?`, hint: 'skip to clear' },
    package: {
      question: `New ${type === 'visa' ? 'visa package' : 'tour package'}${curText}?`,
      hint: 'Pick number or type name',
    },
    passport: { question: `New passport number${curText}?`, hint: 'skip to clear' },
    duration: { question: `New duration${curText}?`, hint: 'Format: 4N5D (4 nights, 5 days)' },
    custom_req: { question: `New custom requirements${curText}?`, hint: 'skip to clear' },
  };
  return prompts[field] || { question: `New ${field}?`, hint: null };
}

export function validateSimpleField(field, raw) {
  const v = String(raw || '').trim();
  if (field === 'name' && !v) return 'Name is required.';
  if (field === 'phone' && !v) return 'Phone is required.';
  if (field === 'package' && !isSkip(v) && v.length < 2) return 'Enter package name or pick from list.';
  if (field === 'duration' && !isSkip(v)) {
    const m = v.match(/^(\d+)\s*[nN]\s*(\d+)\s*[dD]?$/) || v.match(/^(\d+)\s*\/\s*(\d+)$/);
    if (!m) return 'Use format like 4N5D (4 nights, 5 days).';
  }
  return null;
}

function parseTourDuration(raw) {
  const v = String(raw || '').trim();
  const m = v.match(/^(\d+)\s*[nN]\s*(\d+)\s*[dD]?$/) || v.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (!m) return null;
  return `${m[1]}N ${m[2]}D`;
}

export function parseSimpleField(field, raw, { matchSource, normalizePhone, matchPackage }) {
  const v = String(raw || '').trim();
  switch (field) {
    case 'name':
      return { name: capitalizeName(v) };
    case 'phone':
      return { phone: normalizePhone(v) };
    case 'note':
      return { note_text: isSkip(v) ? null : v };
    case 'pnr':
      return { pnr: v.replace(/\s+/g, '').toUpperCase() };
    case 'source':
      return { source: matchSource(v) };
    case 'airline':
      return { airline: isSkip(v) ? null : v };
    case 'package':
      return { package_name: isSkip(v) ? null : (matchPackage ? matchPackage(v) : v) };
    case 'passport':
      return { extra: { passport_no: isSkip(v) ? null : upperDoc(v) } };
    case 'duration':
      return { extra: { tour_duration: isSkip(v) ? null : parseTourDuration(v) } };
    case 'custom_req': {
      const text = isSkip(v) ? null : v;
      return { custom_req: text, is_custom: !!text };
    }
    default:
      return {};
  }
}
